import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';
import { DOCUMENT, documentTextButtonSx } from '../../constants/documentTheme';
import { formatMoney } from '../../utils/partnerFinance';
import { downloadBlob } from '../../utils/groupBilling';
import StripeCheckout from '../StripeCheckout';
import { BillingStatusChip, eyebrowSx } from './BillingParts';

function takeSessionIdFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get('session_id');
  if (!sessionId) return null;
  params.delete('session_id');
  const query = params.toString();
  window.history.replaceState(window.history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
  return sessionId;
}

/** Traveler view of the group invoice their advisor sent. */
const TripGroupInvoice = ({ tripId, onPaid }) => {
  const { t } = useTranslation();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const confirmedRef = useRef(false);

  const base = `/trips/${tripId}/billing`;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`${base}/my-invoice`);
      setInvoice(data?.invoice || null);
    } catch (err) {
      if (err.response?.status !== 404) setError(err.response?.data?.error || t('tripInvoice.loadFailed'));
      setInvoice(null);
    } finally {
      setLoading(false);
    }
  }, [base, t]);

  const confirm = useCallback(async (sessionId) => {
    try {
      await api.post(`${base}/confirm`, { sessionId });
      setNotice(t('tripInvoice.paymentReceived'));
      onPaid?.();
    } catch (err) {
      setError(err.response?.data?.error || t('tripInvoice.confirmFailed'));
    }
  }, [base, onPaid, t]);

  useEffect(() => {
    const run = async () => {
      if (!confirmedRef.current) {
        confirmedRef.current = true;
        const sessionId = takeSessionIdFromUrl();
        if (sessionId) await confirm(sessionId);
      }
      await load();
    };
    run();
  }, [confirm, load]);

  const startCheckout = async (installmentId = null) => {
    try {
      setBusy(installmentId || 'balance');
      setError('');
      const { data } = await api.post(`${base}/checkout`, installmentId ? { installmentId } : {});
      if (data?.clientSecret) setCheckout({ clientSecret: data.clientSecret, sessionId: data.sessionId });
    } catch (err) {
      setError(err.response?.data?.error || t('tripInvoice.checkoutFailed'));
    } finally {
      setBusy(null);
    }
  };

  const reportPaid = async (installment) => {
    try {
      setBusy(`report-${installment.id}`);
      setError('');
      await api.post(`${base}/installments/${installment.id}/reported`);
      setNotice(t('tripInvoice.reported'));
      await load();
    } catch (err) {
      setError(err.response?.data?.error || t('tripInvoice.reportFailed'));
    } finally {
      setBusy(null);
    }
  };

  const downloadPdf = async (inv) => {
    try {
      const { data } = await api.get(`${base}/invoices/${inv.id}/pdf`, { responseType: 'blob' });
      downloadBlob(data, `${inv.number}.pdf`, 'application/pdf');
    } catch {
      setError(t('tripInvoice.pdfFailed'));
    }
  };

  const handleCheckoutSuccess = async () => {
    const sessionId = checkout?.sessionId;
    setCheckout(null);
    if (sessionId) await confirm(sessionId);
    await load();
  };

  if (loading && !invoice) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }} data-testid="trip-group-invoice-loading">
        <CircularProgress size={28} sx={{ color: DOCUMENT.oxblood }} />
      </Box>
    );
  }

  if (!invoice) {
    if (error) return <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>;
    return (
      <Box data-testid="trip-group-invoice-empty" sx={{ py: 6, textAlign: 'center', maxWidth: 480, mx: 'auto' }}>
        <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 28, mb: 1 }}>{t('tripInvoice.title')}</Typography>
        <Typography sx={{ fontSize: 14.5, color: DOCUMENT.ink2, lineHeight: 1.6 }}>
          {t('tripInvoice.empty')}
        </Typography>
      </Box>
    );
  }

  const currency = invoice.currency || 'USD';
  if (!invoice.hasInvoice) {
    const message = invoice.total > 0 && invoice.partnerName
      ? t('tripInvoice.notYet', { partner: invoice.partnerName })
      : t('tripInvoice.empty');
    return (
      <Box data-testid={invoice.total > 0 ? 'trip-group-invoice-pending' : 'trip-group-invoice-empty'} sx={{ py: 6, textAlign: 'center', maxWidth: 480, mx: 'auto' }}>
        <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 28, mb: 1 }}>{t('tripInvoice.title')}</Typography>
        <Typography sx={{ fontSize: 14.5, color: DOCUMENT.ink2, lineHeight: 1.6 }}>
          {message}
        </Typography>
      </Box>
    );
  }

  const openInApp = invoice.invoices
    .filter((inv) => inv.type === 'invoice')
    .flatMap((inv) => inv.installments)
    .filter((i) => i.status !== 'paid' && i.status !== 'void' && i.collectionMethod === 'in_app');

  return (
    <Box data-testid="trip-group-invoice" sx={{ border: `1px solid ${DOCUMENT.ruleHard}`, bgcolor: DOCUMENT.surface, p: { xs: 2, md: 2.5 }, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 22 }}>{t('tripInvoice.title')}</Typography>
          <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3 }}>
            {t('tripInvoice.from', { partner: invoice.partnerName })}
            {invoice.travelers?.length ? ` · ${t('tripInvoice.covers', { names: invoice.travelers.join(', ') })}` : ''}
          </Typography>
        </Box>
        <BillingStatusChip status={invoice.status} testId="trip-group-invoice-status" />
      </Box>

      {error ? <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>{error}</Alert> : null}
      {notice ? <Alert severity="success" sx={{ mt: 2 }} onClose={() => setNotice('')}>{notice}</Alert> : null}

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, my: 2 }}>
        {[['total', invoice.total], ['paid', invoice.paid], ['balance', invoice.balance]].map(([key, value]) => (
          <Box key={key}>
            <Typography sx={eyebrowSx}>{t(`tripInvoice.${key}`)}</Typography>
            <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 20 }} data-testid={`trip-group-invoice-${key}`}>
              {formatMoney(value, currency)}
            </Typography>
          </Box>
        ))}
      </Box>

      {openInApp.length > 1 && invoice.balance > 0 && (
        <Button
          sx={{ ...documentTextButtonSx, mb: 2 }}
          disabled={Boolean(busy)}
          onClick={() => startCheckout(null)}
          data-testid="trip-group-invoice-pay-balance"
        >
          {busy === 'balance' ? t('tripAddOns.buying') : t('tripInvoice.payBalance', { amount: formatMoney(invoice.balance, currency) })}
        </Button>
      )}

      {invoice.invoices.map((inv) => (
        <Box key={inv.id} sx={{ borderTop: `1px solid ${DOCUMENT.rule}`, pt: 1.5, mt: 1.5 }} data-testid={`trip-group-invoice-doc-${inv.id}`}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 600 }}>
              {inv.type === 'credit_note' ? t('tripInvoice.creditNote', { number: inv.number }) : t('tripInvoice.invoiceNumber', { number: inv.number })}
            </Typography>
            <Button size="small" sx={documentTextButtonSx} onClick={() => downloadPdf(inv)} data-testid={`trip-group-invoice-pdf-${inv.id}`}>
              {t('tripInvoice.downloadPdf')}
            </Button>
          </Box>
          {inv.lines.map((line) => (
            <Box key={line.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}>
              <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink2 }}>{line.description}</Typography>
              <Typography sx={{ fontSize: 13.5, fontVariantNumeric: 'tabular-nums' }}>{formatMoney(line.amount, currency)}</Typography>
            </Box>
          ))}

          {inv.installments.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography sx={{ ...eyebrowSx, mb: 0.5 }}>{t('tripInvoice.schedule')}</Typography>
              {inv.installments.map((inst) => {
                const open = inst.status !== 'paid' && inst.status !== 'void';
                return (
                  <Box
                    key={inst.id}
                    data-testid={`trip-group-installment-${inst.id}`}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, py: 1, borderBottom: `1px solid ${DOCUMENT.rule}`, flexWrap: 'wrap' }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: 14 }}>{inst.label}</Typography>
                      <Typography sx={{ fontSize: 12, color: inst.overdue ? DOCUMENT.oxblood : DOCUMENT.ink3 }}>
                        {inst.overdue ? t('tripInvoice.overdueSince', { date: inst.dueDate }) : t('tripInvoice.dueOn', { date: inst.dueDate })}
                        {inst.paid > 0 && open ? ` · ${t('tripInvoice.remaining', { amount: formatMoney(inst.remaining, currency) })}` : ''}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(inst.amount, currency)}</Typography>
                      {!open && <BillingStatusChip status={inst.status} />}
                      {open && inst.collectionMethod === 'in_app' && (
                        <Button
                          sx={documentTextButtonSx}
                          disabled={Boolean(busy)}
                          onClick={() => startCheckout(inst.id)}
                          data-testid={`trip-group-installment-pay-${inst.id}`}
                        >
                          {busy === inst.id ? t('tripAddOns.buying') : t('tripInvoice.pay', { amount: formatMoney(inst.remaining, currency) })}
                        </Button>
                      )}
                      {open && inst.collectionMethod === 'external_link' && inst.paymentLinkUrl && (
                        <>
                          <Button
                            component="a"
                            href={inst.paymentLinkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={documentTextButtonSx}
                            data-testid={`trip-group-installment-link-${inst.id}`}
                          >
                            {inst.paymentLinkLabel || t('tripInvoice.payWithLink')}
                          </Button>
                          {inst.clientReportedAt ? (
                            <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3 }} data-testid={`trip-group-installment-reported-${inst.id}`}>
                              {t('tripInvoice.reportedNote')}
                            </Typography>
                          ) : (
                            <Button
                              sx={documentTextButtonSx}
                              disabled={Boolean(busy)}
                              onClick={() => reportPaid(inst)}
                              data-testid={`trip-group-installment-report-${inst.id}`}
                            >
                              {t('tripInvoice.iPaid')}
                            </Button>
                          )}
                        </>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}

          {inv.offlineInstructions && inv.installments.some((i) => i.collectionMethod === 'offline' && i.status !== 'paid') && (
            <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#fff', border: `1px solid ${DOCUMENT.rule}` }} data-testid={`trip-group-invoice-offline-${inv.id}`}>
              <Typography sx={{ ...eyebrowSx, mb: 0.5 }}>{t('tripInvoice.howToPay')}</Typography>
              <Typography sx={{ fontSize: 13.5, whiteSpace: 'pre-line' }}>{inv.offlineInstructions}</Typography>
            </Box>
          )}
        </Box>
      ))}

      <StripeCheckout
        open={Boolean(checkout?.clientSecret)}
        clientSecret={checkout?.clientSecret}
        tripTitle={t('tripInvoice.title')}
        onClose={() => setCheckout(null)}
        onSuccess={handleCheckoutSuccess}
      />
    </Box>
  );
};

export default TripGroupInvoice;
